import type React from 'react';
import {
  type ChangeEvent,
  type Dispatch,
  type DragEvent,
  type SetStateAction,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { Check, ChevronRight, X } from 'lucide-react';

import { getMediaOptions } from '@/api/media';
import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { env } from '@/constants/env';
import { queryClient } from '@/lib/react-query';
import { cn } from '@/lib/utils';

import { useUploadFloorLayouts } from '../../../api/upload-floor-layout';
import { ACCEPTED_IMAGE_INPUT_TYPE, uploadLayoutsSchema } from '../../../schema/upload-media';
import type { Image } from '../../media-data';
import type { UploadMediaModalType } from '../../upload-media-modal';
import type { FloorPlan, FloorPlanMarker } from '../context';

export interface FloorPlanModalProps {
  images: Image[] | null;
  nodeId?: string;
  estateId: string;
  setSkipFloorPlan: Dispatch<SetStateAction<boolean>>;
  skipIntro?: boolean;
  floorPlan: FloorPlan;
  setFloorPlan: Dispatch<SetStateAction<FloorPlan>>;
}

export const FloorPlanModal: React.FC<FloorPlanModalProps> = ({
  images,
  nodeId,
  estateId,
  setSkipFloorPlan,
  skipIntro = false,
  floorPlan,
  setFloorPlan,
}) => {
  const [showIntro, setShowIntro] = useState(skipIntro);
  const [currentTab, setCurrentTab] = useState<'floorPlanSelection' | 'nodePosition'>(
    'floorPlanSelection'
  );
  const [currentFloorPlanImage, setCurrentFloorPlanImage] = useState<Image | null>(floorPlan.image);

  const getModalTitle = () => {
    switch (currentTab) {
      case 'floorPlanSelection':
        return t('selectFloorPlan');
      case 'nodePosition':
        return t('updateHotspotLocationOnFloorplan');
      default:
        return '';
    }
  };

  const RenderContent = () => {
    // No floor plan selected
    if (!floorPlan.image && !showIntro) {
      return (
        <div>
          <div className="flex flex-row gap-6 px-6 py-4">
            <div className="w-full space-y-5 pt-6">
              <p className="text-[16px] text-lg">
                Make the most of your 360° Tour & upload floorplan!
              </p>
              <p className="text-base">
                A floor plan enhances the virtual tour experience by providing a clear property
                overview. It helps visitors understand room layouts, navigate easily between spaces,
                and better plan their virtual walkthrough.
              </p>
              <div className="flex flex-col gap-1">
                <div>
                  <ChevronRight strokeWidth={3} className="inline-block text-primary-light" />
                  set anchors easily
                </div>
                <div>
                  <ChevronRight strokeWidth={3} className="inline-block text-primary-light" />
                  comfy navigation
                </div>
                <div>
                  <ChevronRight strokeWidth={3} className="inline-block text-primary-light" />
                  customers think it's cool
                </div>
              </div>
            </div>
            <div className="flex h-auto w-full overflow-hidden rounded border border-[#061B25]">
              <img
                className="size-full object-cover"
                src={`${env.API_URL}/images/tutorial-virtualtour-floorpan-with-pins?size=thumbnail500`}
                alt="floorplan-3x2.jpg"
              />
            </div>
          </div>
          <div className="flex flex-row justify-center gap-x-3 px-6 py-5">
            <Button className="w-full max-w-52" onClick={() => setShowIntro(true)}>
              {t('Upload')}
            </Button>
            <Button
              className="w-full max-w-52"
              variant="outline"
              onClick={() => {
                setSkipFloorPlan(true);
                closeModal();
              }}
            >
              {t('Later')}
            </Button>
          </div>
        </div>
      );
    }

    const RenderUploadTab = () => {
      const { t } = useTranslation();
      const acceptedType = ACCEPTED_IMAGE_INPUT_TYPE;
      const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
      } = useForm<UploadMediaModalType>({
        resolver: zodResolver(uploadLayoutsSchema()),
        defaultValues: {
          files: [],
        },
      });

      const [selectedImage, setSelectedImage] = useState<Image>();
      const onImageClick = (image: Image) => () => {
        removeFile();
        setSelectedImage(image);
      };

      const [, setDragActive] = useState(false);
      const files = watch('files');

      const fileInputChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const newFile = Array.from(event.target.files || []);

        reset({ files: newFile });
      };

      const handleDragOver = (event: DragEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setDragActive(true);
      };

      const handleDragLeave = () => {
        setDragActive(false);
      };

      const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setDragActive(false);

        const newFiles = Array.from(event.dataTransfer.files || []);

        reset({ files: newFiles });
      };

      const removeFile = () => {
        reset({ files: [] });
      };

      const { mutate: uploadFloorLayouts } = useUploadFloorLayouts({
        mutationConfig: {
          onSuccess: (e) => {
            const newFile = Object.entries(e).flatMap((item) => item[1]);
            queryClient.invalidateQueries({ queryKey: getMediaOptions(estateId).queryKey });
            setFloorPlan({
              ...floorPlan,
              image: newFile[0] as Image | null,
              id: newFile[0].mediaId,
            });
          },
        },
      });

      const submitHandler = async (data: UploadMediaModalType) => {
        // Convert File[] to FileList
        const dataTransfer = new DataTransfer();
        data.files.forEach((file) => dataTransfer.items.add(file));
        await uploadFloorLayouts({ layouts: dataTransfer.files, estateId: estateId });
        closeModal();
      };

      const onSubmit = () => {
        if (selectedImage) {
          setFloorPlan({
            ...floorPlan,
            image: selectedImage,
            id: selectedImage.mediaId,
            markers: [],
          });
          if (nodeId) {
            setCurrentFloorPlanImage(selectedImage);
            setCurrentTab('nodePosition');
            return;
          }
          closeModal();
          return;
        }

        handleSubmit(submitHandler)();
      };

      const RenderPreview = () => {
        if (files && files.length > 0) {
          return (
            <div className="relative w-full">
              <button
                type="button"
                className="absolute right-4 top-4 rounded-full text-primary-light"
                onClick={() => removeFile()}
              >
                <X />
              </button>
              <img
                src={URL.createObjectURL(files[0])}
                alt={`Preview ${files[0].name}`}
                className="mx-auto size-full max-h-64 max-w-sm rounded object-contain py-6"
              />
            </div>
          );
        }

        if (selectedImage) {
          return (
            <div className="relative w-full">
              <button
                type="button"
                className="absolute right-4 top-4 rounded-full text-primary-light"
                onClick={() => setSelectedImage(undefined)}
              >
                <X />
              </button>
              <img
                className="mx-auto size-full max-h-64 max-w-sm rounded object-contain py-6"
                src={`${env.API_URL}/images/${selectedImage.mediaId}?size=thumbnail200`}
                alt={selectedImage.fileName || ''}
              />
            </div>
          );
        }

        return (
          <label
            htmlFor="dropzone-file"
            className="flex h-full w-full flex-grow cursor-pointer flex-col items-center justify-center rounded-lg hover:bg-secondary"
          >
            <div className="flex flex-col items-center justify-center p-6">
              <svg
                className="mb-4 size-8 text-text-secondary"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 16"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                />
              </svg>
              <p className="mb-1 text-center text-sm text-text-secondary">
                {t('uploadDocumentText')}
              </p>
              <p className={cn('text-sm text-text-secondary', errors?.files && 'text-danger')}>
                {acceptedType}
              </p>
            </div>
            <Input
              id="dropzone-file"
              type="file"
              accept={acceptedType}
              className="hidden"
              {...register('files', { onChange: fileInputChangeHandler })}
            />
          </label>
        );
      };

      return (
        <div className="p-8">
          <form className="mx-auto">
            <button
              type="button"
              className="relative flex h-full max-h-64 min-h-64 w-full flex-col items-center justify-center overflow-hidden rounded-lg"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="pointer-events-none absolute inset-0 rounded-lg border-2 border-dashed border-text-secondary"></div>
              <RenderPreview />
            </button>
            <div>
              {images && images.length > 0 && (
                <>
                  <div className="my-5 text-center">
                    <span>{t('realEstateFloorPlans')}</span>
                  </div>
                  <div className="grid max-h-96 w-full grid-cols-3 gap-1 overflow-y-scroll rounded">
                    {images.map((image) => (
                      <button
                        type="button"
                        key={image.id}
                        className="relative h-32 cursor-pointer overflow-hidden rounded"
                        onClick={onImageClick(image)}
                      >
                        {selectedImage?.id === image.id && (
                          <div className="absolute right-2 top-2 flex size-4 items-center justify-center rounded-full bg-primary-dark">
                            <Check strokeWidth={5} className="size-2 text-white" />
                          </div>
                        )}
                        <img
                          className="size-full object-contain"
                          src={`${env.API_URL}/images/${image.mediaId}?size=thumbnail200`}
                          alt={image.fileName || ''}
                        />
                      </button>
                    ))}
                  </div>
                </>
              )}
              <div className="mt-8 grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  className="border-red-500 text-red-500"
                  onClick={closeModal}
                >
                  {t('cancel')}
                </Button>
                <Button disabled={!files.length && !selectedImage} onClick={onSubmit}>
                  {t('submit')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      );
    };

    const RenderSetMarkerPostion = () => {
      const [marker, setMarker] = useState<{
        nodeId?: string;
        position: { x: number; y: number };
      }>();
      const imageRef = useRef<HTMLImageElement>(null);

      return (
        <div className="py-10">
          <div className="relative">
            {/** biome-ignore lint/a11y/useKeyWithClickEvents: ignored */}
            <img
              ref={imageRef}
              alt={`Floor-plan-${floorPlan.image?.fileName}`}
              onClick={(e) => {
                const img = imageRef.current;
                if (!img) return;

                const rect = img.getBoundingClientRect();

                // Get clicked position relative to the image itself
                const x = (e.nativeEvent.offsetX / rect.width) * 100;
                const y = (e.nativeEvent.offsetY / rect.height) * 100;

                setMarker({
                  nodeId: nodeId,
                  position: { x, y },
                });
              }}
              className="w-full cursor-pointer object-contain"
              src={`${env.API_URL}/images/${floorPlan.image?.mediaId}?size=spherical`}
            />
            {marker && (
              <div
                className="pointer-events-none absolute size-7 rounded-full border-[3px] border-primary-light bg-white"
                style={{
                  left: `${marker.position.x}%`,
                  top: `${marker.position.y}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              ></div>
            )}
            {floorPlan.markers?.map((marker: FloorPlanMarker) => {
              if (marker.nodeId === nodeId) return null;

              return (
                <div
                  key={marker.nodeId}
                  className="absolute size-6 cursor-pointer rounded-full border-[3px] border-[#061B25] bg-primary-light"
                  style={{
                    left: `${marker.position.x}%`,
                    top: `${marker.position.y}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                ></div>
              );
            })}
          </div>
          <div className="flex justify-center gap-4 pt-10">
            <Button variant="outline" className="border-red-500 text-red-500" onClick={closeModal}>
              {t('cancel')}
            </Button>
            <Button
              onClick={() => {
                setFloorPlan((prev) => {
                  const updatedMarkers = prev.markers.filter(
                    (item: FloorPlanMarker) => item.nodeId !== marker?.nodeId
                  );
                  return {
                    ...prev,
                    markers: [...updatedMarkers, marker],
                  };
                });
                closeModal();
              }}
              disabled={!marker}
            >
              {t('submit')}
            </Button>
          </div>
        </div>
      );
    };

    if (currentFloorPlanImage && nodeId) {
      setCurrentTab('nodePosition');
      return <RenderSetMarkerPostion />;
    }

    return <RenderUploadTab />;
  };

  return (
    <ModalBase
      overlayWrapperClassname="z-[99999999]"
      className="flex h-full w-full max-w-screen-md flex-col overflow-y-auto bg-transparent p-0 max-md:p-3"
    >
      <div className="my-auto h-auto rounded-b bg-white">
        <div className="flex justify-between rounded-t bg-[#061B25] px-5 py-4 text-white">
          <span className="text-base">{getModalTitle()}</span>
          <button type="button" onClick={() => closeModal()}>
            <X />
          </button>
        </div>
        <RenderContent />
      </div>
    </ModalBase>
  );
};
